import {
  type ColorLegendPosition,
  type ID,
  type Offset,
  ProjectActions,
  type ProjectTheme,
  type Size,
  type StickyNoteMovePayload,
  type TextSize,
  type TreeFrame,
  type TreeType,
} from '@octopus-do/octopus-project-core'
import { thunkAddSection } from '@store/migrationActions/thunkActions/thunkSectionsActions'
import type { AppState } from '@store/types'
import { getId } from '@store/utils'
import { useDispatch } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

type CommitAction = {
  type: string
  payload: any
}

export const useProjectLegacyActions = () => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const commit = (actions: CommitAction[], withRequest = true) => {
    if (withRequest) {
      dispatch({
        type: 'COMMIT_PROJECT_REQUEST',
        payload: actions,
      })
    } else {
      actions.forEach((action) => {
        dispatch(action)
      })
    }
  }

  return {
    commit,

    // ------------------------------------------------
    // nodes
    updateNodeTitle: ({ id, title }: { id: string; title: string }) => {
      commit([ProjectActions.nodes.updateTitle({ id, title })])
    },

    // ------------------------------------------------
    // blocks

    // ------------------------------------------------
    // tabs
    moveUpTab: ({ id }: { id: string }) => {
      commit([ProjectActions.tabs.orderUp({ id })])
    },
    moveDownTab: ({ id }: { id: string }) => {
      commit([ProjectActions.tabs.orderDown({ id })])
    },
    renameTab: ({ id, name }: { id: string; name: string }) => {
      commit([ProjectActions.tabs.updateTitle({ id, title: name })])
    },
    deleteTab: ({ id }: { id: string }) => {
      commit([ProjectActions.tabs.delete({ ids: [id] })])
    },
    duplicateTab: ({ id }: { id: string }) => {
      const cloneInitiateId = getId()
      commit([ProjectActions.tabs.clone({ id, initiator: cloneInitiateId })])
    },

    // ------------------------------------------------
    // sections

    addSection: ({ id }: { id: string }) => {
      dispatch(thunkAddSection(id))
    },

    moveSectionUp: ({ id }: { id: string }) => {
      commit([ProjectActions.sections.orderUp({ id })])
    },
    moveSectionDown: ({ id }: { id: string }) => {
      commit([ProjectActions.sections.orderDown({ id })])
    },

    deleteSections: ({ ids }: { ids: string[] }) => {
      commit([ProjectActions.sections.delete({ ids })])
    },

    collapseSections: ({
      ids,
      collapsed,
    }: {
      ids: string[]
      collapsed: boolean
    }) => {
      commit([
        ProjectActions.sections.changeCollapse({
          ids,
          collapsed,
        }),
      ])
    },
    changeSectionColor: ({ id, colorId }: { id: string; colorId: string }) => {
      commit([ProjectActions.sections.changeColor({ id, colorId })])
    },
    changeSectionTitle: ({ id, title }: { id: string; title: string }) => {
      commit([ProjectActions.sections.updateTitle({ id, title })])
    },
    changeSectionTextSize: ({
      id,
      textSize,
    }: {
      id: string
      textSize: TextSize
    }) => {
      commit([ProjectActions.sections.changeTextSize({ id, textSize })])
    },

    // ------------------------------------------------
    // project settings

    renameProject: ({ title }: { title: string }) => {
      commit([ProjectActions.settings.updateTitle({ title })])
    },
    changeProjectLegendPosition: ({
      legendPosition,
    }: {
      legendPosition: ColorLegendPosition
    }) => {
      commit([ProjectActions.settings.changeLegendPosition({ legendPosition })])
    },
    changeProjectTheme: ({ theme }: { theme: ProjectTheme }) => {
      commit([ProjectActions.settings.changeTheme({ theme })])
    },
    changeProjectFrameType: ({ frame }: { frame: TreeFrame }) => {
      commit([ProjectActions.settings.changeTreeFrame({ frame })])
    },
    changeProjectTreeType: ({ tree }: { tree: TreeType }) => {
      commit([ProjectActions.settings.changeTreeType({ tree })])
    },

    // ------------------------------------------------
    // sticky notes
    changeStickyNoteColor: ({ id, color }: { id: string; color: string }) => {
      commit([ProjectActions.stickyNotes.updateColor({ id, color })])
    },
    updateStickyNoteOffset: ({
      id,
      offset,
    }: {
      id: string
      offset: Offset
    }) => {
      commit([ProjectActions.stickyNotes.updateOffset({ id, offset })])
    },
    // the note changes hands: a page, a tree section or its tab (OCT-5256)
    moveStickyNote: ({ id, nextParentId, offset }: StickyNoteMovePayload) => {
      commit([ProjectActions.stickyNotes.move({ id, nextParentId, offset })])
    },
    updateStickyNoteSize: ({ id, size }: { id: string; size: Size }) => {
      commit([ProjectActions.stickyNotes.updateSize({ id, size })])
    },
    changeStickyNoteTextSize: ({
      id,
      textSize,
    }: {
      id: ID
      textSize: TextSize
    }) => {
      commit([ProjectActions.stickyNotes.updateTextSize({ id, textSize })])
    },

    // ------------------------------------------------
    // colors
    // @todo implement one way with index
    updateColorOrder: ({ id, order }: { id: ID; order: number }) => {
      commit([ProjectActions.colors.updateOrder({ id, order })])
    },
    updateColorHexCode: ({ id, hexCode }: { id: ID; hexCode: string }) => {
      commit([ProjectActions.colors.updateCode({ id, hexCode })])
    },
    changeColorTitle: ({ id, title }: { id: string; title: string }) => {
      commit([ProjectActions.colors.updateTitle({ id, title })])
    },
    changeIsShowInLegend: ({
      id,
      showInLegend,
    }: {
      id: string
      showInLegend: boolean
    }) => {
      commit([ProjectActions.colors.updateShowInLegend({ id, showInLegend })])
    },
  }
}
